import { useEffect, useRef, useState } from 'react';
import { MapPin } from 'lucide-react';
import { loadGoogleMapsApi } from '@/lib/googleMaps';

interface RestaurantMapProps {
  lat: number;
  lng: number;
  name: string;
  zoom?: number;
}

const RestaurantMap = ({ lat, lng, name, zoom = 15 }: RestaurantMapProps) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [map, setMap] = useState<any>(null);

  // Google Maps laden
  useEffect(() => {
    let isMounted = true;

    const initMap = async () => {
      try {
        await loadGoogleMapsApi();
        if (isMounted) {
          setMapLoaded(true);
        }
      } catch (error) {
        console.error('Fehler beim Laden von Google Maps:', error);
      }
    };

    initMap();

    return () => {
      isMounted = false;
    };
  }, []);

  // Karte initialisieren, wenn Google Maps geladen ist
  useEffect(() => {
    if (!mapLoaded || !mapRef.current || !window.google?.maps) return;

    let currentMap: any = null;

    try {
      const mapOptions = {
        center: { lat, lng },
        zoom: zoom,
        mapTypeControl: false,
        fullscreenControl: false,
        streetViewControl: false,
        zoomControl: true // Vereinfachte Zoom-Kontrolle ohne Position
      };

      const newMap = new window.google.maps.Map(mapRef.current, mapOptions);
      currentMap = newMap;

      // Warte kurz, bis die Karte initialisiert ist
      setTimeout(() => {
        try {
          // Marker hinzufügen (aktualisiert für neue API)
          let marker;
          if (window.google.maps.marker?.AdvancedMarkerElement) {
            console.log("Verwende neue AdvancedMarkerElement API");
            marker = new window.google.maps.marker.AdvancedMarkerElement({
              map: newMap,
              position: { lat, lng },
              title: name
            });
          } else {
            console.warn("AdvancedMarkerElement nicht verfügbar, verwende veraltete Marker API");
            marker = new window.google.maps.Marker({
              map: newMap,
              position: { lat, lng },
              title: name
            });
          }

          // InfoWindow mit Restaurantinformationen
          const infoWindow = new window.google.maps.InfoWindow({
            content: `
              <div style="padding: 8px; max-width: 250px;">
                <h3 style="margin: 0 0 8px 0; font-size: 16px; font-weight: 600;">${name}</h3>
                <div style="display: flex; align-items: center; margin-bottom: 4px;">
                  <span style="color: #16a34a; margin-right: 8px;">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M12 22s-8-4.5-8-11.8a8 8 0 0 1 16 0c0 7.3-8 11.8-8 11.8z"/>
                      <circle cx="12" cy="10" r="3"/>
                    </svg>
                  </span>
                  <a href="https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}" target="_blank" style="color: #16a34a; text-decoration: none; font-size: 14px;">
                    Routenplanung starten
                  </a>
                </div>
              </div>
            `
          });

          // InfoWindow beim Klick auf den Marker öffnen
          marker.addListener('click', () => {
            infoWindow.open(newMap, marker);
          });

          // Anfänglich InfoWindow öffnen
          infoWindow.open(newMap, marker);

          setMap(newMap);
        } catch (error) {
          console.error('Fehler beim Hinzufügen des Markers:', error);
        }
      }, 100);
    } catch (error) {
      console.error('Fehler beim Initialisieren der Karte:', error);
    }

    return () => {
      if (currentMap) {
        // Aufräumen, wenn die Komponente unmountet wird
        const markers = currentMap.__markers || [];
        markers.forEach((marker: any) => marker.setMap(null));
      }
    };
  }, [mapLoaded, lat, lng, name, zoom]);

  // Karte aktualisieren, wenn sich die Koordinaten ändern
  useEffect(() => {
    if (!map || !lat || !lng || !window.google?.maps) return;

    try {
      map.setCenter({ lat, lng });
      
      // Alle vorhandenen Marker entfernen
      const markers = map.__markers || [];
      markers.forEach((marker: any) => marker.setMap(null));
      
      // Neuen Marker hinzufügen (aktualisiert für neue API)
      let marker;
      if (window.google.maps.marker?.AdvancedMarkerElement) {
        marker = new window.google.maps.marker.AdvancedMarkerElement({
          map: map,
          position: { lat, lng },
          title: name
        });
      } else {
        marker = new window.google.maps.Marker({
          map: map,
          position: { lat, lng },
          title: name
        });
      }
      
      map.__markers = [marker];
    } catch (error) {
      console.error('Fehler beim Aktualisieren der Karte:', error);
    }
  }, [map, lat, lng, name]);

  return (
    <div className="relative w-full h-full">
      {!mapLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          <div className="animate-pulse flex flex-col items-center">
            <MapPin className="h-8 w-8 text-primary mb-2" />
            <span className="text-gray-500">Karte wird geladen...</span>
          </div>
        </div>
      )}
      <div
        ref={mapRef}
        className="w-full h-full"
        style={{ display: mapLoaded ? 'block' : 'none' }}
      />
    </div>
  );
};

export default RestaurantMap;